import React, { useLayoutEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import FadeInSection from './FadeInSection';
import '../styles/Sellers.scss';

const STEPS = [
  {
    number: '01',
    title: 'Initial Consultation & Pricing',
    duration: '1–2 weeks',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M3 17V7l8-4 8 4v10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="8" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M5 9l6-3 6 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
    description:
      'Lily will walk through your home, review comparable sales, and develop a pricing strategy tailored to current market conditions. Pricing it right from the start drives more showings and stronger offers.',
    tips: [
      'Overpricing leads to longer days on market and lower final sales price',
      'Lily provides a detailed Comparative Market Analysis (CMA)',
      'Discuss your timeline, priorities, and any seller concessions upfront',
    ],
  },
  {
    number: '02',
    title: 'Prepare Your Home',
    duration: '1–4 weeks',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M14 4l4 4-8 8H6v-4l8-8z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 6l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M3 19h16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    description:
      'First impressions matter. Lily will recommend targeted repairs, staging, and decluttering to maximize your home\'s appeal. Many Bay Area sellers complete pre-listing inspections to avoid surprises during escrow.',
    tips: [
      'A pre-listing inspection helps you price accurately and disclose properly',
      'Fresh paint, cleaned carpets, and curb appeal yield strong ROI',
      'Consider professional staging — it typically sells homes faster and for more',
    ],
  },
  {
    number: '03',
    title: 'Photography & Marketing',
    duration: '3–7 days',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="2" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <circle cx="11" cy="12.5" r="3.5" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M8 6V5a1 1 0 011-1h4a1 1 0 011 1v1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="16" cy="9" r="1" fill="currentColor"/>
      </svg>
    ),
    description:
      'Professional photography, a 3D tour, and compelling copy are prepared before your home goes live. Lily markets across the MLS, major portals, social media, and her network of active buyers and agents.',
    tips: [
      'Twilight and drone photos are standard for premium Bay Area listings',
      'A "coming soon" period can build anticipation before the active date',
      'Lily\'s buyer network can generate pre-market interest',
    ],
  },
  {
    number: '04',
    title: 'Go Live & Showings',
    duration: '1–3 weeks',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="3" y="4" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M3 9h16" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M8 2v3M14 2v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <rect x="7" y="12" width="3" height="3" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
        <rect x="12" y="12" width="3" height="3" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
      </svg>
    ),
    description:
      'Your home is listed on the MLS. Lily coordinates all showings, hosts open houses, and keeps you informed after each visit. In competitive markets, offers are often reviewed after a set review date.',
    tips: [
      'Keep the home show-ready throughout — buyers often return before offering',
      'Lily collects agent feedback to adjust strategy if needed',
      'A review date creates a structured offer deadline that encourages competition',
    ],
  },
  {
    number: '05',
    title: 'Review & Negotiate Offers',
    duration: '1–5 days',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M7 12c0 0 1.5-2 4-2s4 2 4 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M8 8.5h.01M14 8.5h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    description:
      'Lily will present every offer and walk you through price, contingencies, down payment strength, and closing timeline. The highest offer isn\'t always the best — cash buyers and waived contingencies carry real value.',
    tips: [
      'A larger down payment signals a stronger buyer',
      'Contingency waivers reduce risk of the deal falling through',
      'Counter-offers and multiple-counter situations are common in the Bay Area',
    ],
  },
  {
    number: '06',
    title: 'Open Escrow',
    duration: 'Day 1 of escrow',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="4" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M8 10V7a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="11" cy="15" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
      </svg>
    ),
    description:
      'Once you accept an offer, a neutral escrow company is opened to manage the transaction. The buyer deposits their earnest money (typically 3%) within 3 business days. Escrow coordinates with both agents, the lender, and title.',
    tips: [
      'Review the purchase contract thoroughly before signing',
      'Ensure the escrow timeline aligns with your move-out plans',
      'Keep all documentation organized and accessible',
    ],
  },
  {
    number: '07',
    title: 'Disclosures & Contingencies',
    duration: '7–17 days',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="5" y="2" width="12" height="18" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M9 2v2h4V2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M8 9h6M8 13h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M14 15l1 1 2-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    description:
      'You are required to disclose all known material facts about the property. The buyer may conduct their own inspections during their contingency period. Lily will guide you through the Transfer Disclosure Statement and Natural Hazard Disclosure.',
    tips: [
      'Full, accurate disclosure protects you from legal liability after closing',
      'In SF, sellers typically provide inspection reports before listing',
      'Contingency removal signals the buyer\'s full commitment to purchase',
    ],
  },
  {
    number: '08',
    title: 'Close Escrow',
    duration: 'Closing day',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <circle cx="8" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M11.5 10H19v3h-2v3h-3v-3h-2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="8" cy="10" r="1" fill="currentColor"/>
      </svg>
    ),
    description:
      'After the buyer\'s loan funds and all documents are signed, the deed records with the county and the sale is complete. Proceeds are typically wired to you the same day or the next business day.',
    tips: [
      'Complete your move-out before the final walkthrough',
      'Leave appliance manuals and keys in an agreed-upon location',
      'Proceeds are wired after recording — typically within 24 hours',
    ],
  },
];

const STATS = [
  { value: '6–10 wks', label: 'Typical timeline to close' },
  { value: '21–30', label: 'Days in escrow' },
  { value: '2–3 wks', label: 'Average listing period' },
  { value: '$1.35M', label: 'Bay Area median sale price' },
];

export default function Sellers() {
  const [activeStep, setActiveStep] = useState(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="sellers-page">
      <Helmet>
        <title>Bay Area Seller's Guide | Lily Lai Real Estate</title>
        <meta name="description" content="A step-by-step guide to selling your home in the Bay Area. From pricing strategy to closing day, Lily Lai walks you through all 8 steps of the process." />
        <link rel="canonical" href="https://www.lilylaisf.com/sellers" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.lilylaisf.com/sellers" />
        <meta property="og:title" content="Bay Area Seller's Guide — 8 Steps to Sold | Lily Lai Real Estate" />
        <meta property="og:description" content="From pricing strategy to closing day — a complete roadmap for selling your Bay Area home, step by step." />
        <meta property="og:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bay Area Seller's Guide — 8 Steps to Sold | Lily Lai Real Estate" />
        <meta name="twitter:description" content="From pricing strategy to closing day — a complete roadmap for selling your Bay Area home, step by step." />
        <meta name="twitter:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.lilylaisf.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Bay Area Seller's Guide", "item": "https://www.lilylaisf.com/sellers" }
                ]
              },
              {
                "@type": "Article",
                "mainEntityOfPage": "https://www.lilylaisf.com/sellers",
                "headline": "The Bay Area Seller's Guide",
                "description": "A complete step-by-step guide to selling a home in the Bay Area, from pricing strategy and preparation to closing escrow.",
                "image": "https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr",
                "articleSection": "Seller Resources",
                "author": {
                  "@type": "Person",
                  "name": "Lily Lai",
                  "url": "https://www.lilylaisf.com/about"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Lily Lai Real Estate",
                  "url": "https://www.lilylaisf.com/"
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      {/* ── Header ── */}
      <div className="sellers-header">
        <FadeInSection>
          <p className="sellers-eyebrow">Seller Resources</p>
          <h1 className="sellers-title">The Bay Area Seller's Guide</h1>
          <p className="sellers-subtitle">
            Selling in the Bay Area takes more than putting a sign in the yard. Here's exactly what to expect, step by step.
          </p>
          <p className="sellers-meta">8 steps · typically 6–10 weeks start to close</p>
        </FadeInSection>
      </div>

      {/* ── Stats strip ── */}
      <FadeInSection>
        <div className="sellers-stats">
          {STATS.map((s) => (
            <div key={s.label} className="sellers-stat">
              <span className="sellers-stat-value">{s.value}</span>
              <span className="sellers-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </FadeInSection>

      {/* ── Step tracker ── */}
      <FadeInSection>
        <div className="sellers-tracker">
          {STEPS.map((step, i) => (
            <button
              key={step.number}
              className={`sellers-tracker-step${activeStep === i ? ' active' : ''}`}
              onClick={() => {
                setActiveStep(activeStep === i ? null : i);
                if (typeof window.gtag === 'function') window.gtag('event', 'sellers_step_expand', { step_name: step.title });
              }}
              aria-label={step.title}
              title={step.title}
            >
              <span className="sellers-tracker-num">{step.number}</span>
              <span className="sellers-tracker-label">{step.title}</span>
            </button>
          ))}
        </div>
      </FadeInSection>

      {/* ── Timeline ── */}
      <div className="sellers-timeline-wrap">
        <FadeInSection>
          <div className="sellers-timeline">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.number}
                className={`sellers-step${activeStep === i ? ' sellers-step--open' : ''}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
              >
                <button
                  className="sellers-step-header"
                  onClick={() => {
                    setActiveStep(activeStep === i ? null : i);
                    if (typeof window.gtag === 'function') window.gtag('event', 'sellers_step_expand', { step_name: step.title });
                  }}
                  aria-expanded={activeStep === i}
                >
                  <div className="sellers-step-number">{step.number}</div>
                  <div className="sellers-step-icon">{step.icon}</div>
                  <div className="sellers-step-meta">
                    <span className="sellers-step-title">{step.title}</span>
                    <span className="sellers-step-duration">{step.duration}</span>
                  </div>
                  <div className={`sellers-step-chevron${activeStep === i ? ' open' : ''}`} aria-hidden="true">
                    <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
                      <path d="M1 1l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {activeStep === i && (
                    <motion.div
                      className="sellers-step-body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="sellers-step-body-inner">
                        <p className="sellers-step-desc">{step.description}</p>
                        <ul className="sellers-step-tips">
                          {step.tips.map((tip, j) => (
                            <li key={j}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </FadeInSection>
      </div>

      {/* ── CTA strip ── */}
      <FadeInSection>
        <div className="sellers-cta-strip">
          <div className="sellers-cta-content">
            <p className="sellers-cta-heading">Ready to list your home?</p>
            <p className="sellers-cta-sub">Lily will help you get the best price, on your timeline.</p>
          </div>
          <div className="sellers-cta-actions">
            <a
              href="/contact"
              className="sellers-cta-btn sellers-cta-btn--primary"
              onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'sellers_cta_click', { button_label: 'Get in Touch' }); }}
            >Get in Touch</a>
            <a
              href="/home-valuation"
              className="sellers-cta-btn sellers-cta-btn--secondary"
              onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'sellers_cta_click', { button_label: 'Get a Home Valuation' }); }}
            >Get a Home Valuation</a>
          </div>
        </div>
      </FadeInSection>
    </div>
  );
}
